import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { 
  FiX, 
  FiShield, 
  FiPlus, 
  FiTrash2, 
  FiBriefcase, 
  FiShare2, 
  FiTrendingUp, 
  FiLock,
  FiExternalLink,
  FiUser
} = FiIcons;

const DigitalAssetManagerModal = ({ isOpen, onClose, assets, setAssets, successor }) => {
  const [activeTab, setActiveTab] = useState('banking');
  const [isAdding, setIsAdding] = useState(false);

  const handleAddAsset = (e) => {
    e.preventDefault();
    setIsAdding(true);
    setTimeout(() => {
      const newId = Date.now();
      const newAsset = {
        id: newId,
        name: 'New Asset Entry',
        account: '**** 0000',
        status: 'Awaiting Verification'
      };
      
      setAssets(prev => ({
        ...prev,
        [activeTab]: [newAsset, ...prev[activeTab]]
      }));
      setIsAdding(false);
    }, 1000);
  };

  const removeAsset = (tab, id) => {
    setAssets(prev => ({
      ...prev,
      [tab]: prev[tab].filter(asset => asset.id !== id)
    }));
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'banking', label: 'Banking', icon: FiBriefcase },
    { id: 'social', label: 'Social Media', icon: FiShare2 },
    { id: 'investments', label: 'Investments', icon: FiTrendingUp }
  ];

  return (
    <div className="fixed inset-0 z-[160] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl overflow-hidden border border-brand-dark flex flex-col max-h-[90vh]"
      >
        {/* Header */}
        <div className="p-8 md:p-10 border-b border-brand-light flex justify-between items-center bg-brand-light/30 shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-accent-navy text-white rounded-2xl flex items-center justify-center shadow-lg shadow-accent-navy/10">
              <SafeIcon icon={FiShield} className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-2xl font-serif text-accent-navy">Digital Asset Inventory</h3>
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Encrypted vault for account management</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white rounded-full text-text-muted transition-colors border border-transparent hover:border-brand-dark">
            <SafeIcon icon={FiX} className="w-6 h-6" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex px-8 py-4 gap-2 bg-white border-b border-brand-light shrink-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${
                activeTab === tab.id 
                  ? 'bg-accent-navy text-white shadow-md' 
                  : 'text-text-muted hover:bg-brand-light'
              }`}
            >
              <SafeIcon icon={tab.icon} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-8 overflow-y-auto flex-grow space-y-6 custom-scrollbar">
          <div className="flex justify-between items-center">
            <h4 className="text-[10px] font-black uppercase tracking-widest text-accent-navy">
              Current {activeTab} Assets
            </h4>
            <button 
              onClick={handleAddAsset}
              disabled={isAdding}
              className="flex items-center gap-2 text-accent-gold hover:text-accent-navy transition-colors text-[10px] font-black uppercase tracking-widest"
            >
              {isAdding ? (
                <div className="w-3 h-3 border-2 border-accent-gold/30 border-t-accent-gold rounded-full animate-spin" />
              ) : (
                <SafeIcon icon={FiPlus} />
              )}
              Register New Asset
            </button>
          </div>

          <div className="space-y-3">
            <AnimatePresence mode="popLayout">
              {assets[activeTab].map((asset) => (
                <motion.div 
                  key={asset.id}
                  layout
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="flex items-center justify-between p-5 bg-white border border-brand-dark rounded-3xl group hover:border-accent-gold transition-all"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-brand-light rounded-2xl flex items-center justify-center text-accent-navy group-hover:bg-accent-gold/10 group-hover:text-accent-gold transition-colors">
                      <SafeIcon icon={tabs.find(t => t.id === activeTab)?.icon || FiLock} className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-bold text-accent-navy">{asset.name}</p>
                        <span className={`text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full ${
                          asset.status === 'Verified' || asset.status === 'Linked' || asset.status === 'Memorialized'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          {asset.status}
                        </span>
                      </div>
                      <p className="text-[10px] text-text-muted font-medium uppercase tracking-tighter">
                        {asset.account || asset.handle || asset.type} • Last sync: 2 days ago
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button className="p-2 text-text-muted hover:text-accent-navy transition-colors">
                      <SafeIcon icon={FiExternalLink} />
                    </button>
                    <button 
                      onClick={() => removeAsset(activeTab, asset.id)}
                      className="p-2 text-text-muted hover:text-red-500 transition-colors"
                    >
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {assets[activeTab].length === 0 && (
              <div className="text-center py-12 bg-brand-light/20 rounded-[40px] border-2 border-dashed border-brand-dark">
                <SafeIcon icon={FiLock} className="w-8 h-8 text-text-muted/30 mx-auto mb-4" />
                <p className="text-sm font-bold text-accent-navy">No assets registered</p>
                <p className="text-[10px] text-text-muted uppercase mt-1">Start by adding your first {activeTab} account</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-8 border-t border-brand-light bg-brand-light/30 shrink-0">
          <div className="flex items-center gap-4 bg-white p-4 rounded-2xl border border-brand-dark">
            <div className="w-10 h-10 bg-accent-gold/10 text-accent-gold rounded-xl flex items-center justify-center shrink-0">
              <SafeIcon icon={FiUser} className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy">Designated Successor</p>
              <p className="text-xs text-text-muted">
                Digital assets will be transferred to <strong>{successor ? successor.email : 'Sarah Jenkins (Heir)'}</strong> upon activation.
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default DigitalAssetManagerModal;